import React from 'react';
import { useIntl } from 'react-intl';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { Button } from '@daedalus/atlas/Button';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { InputButton } from '@daedalus/atlas/helpers/InputButton';
import { Icon } from '@daedalus/atlas/Icon';
import { Text } from '@daedalus/atlas/Text';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { dateStringToMiddayDate } from '@daedalus/core/src/utils/date';
import { datePickerMessages } from '../../search/DatePicker/helpers/datePickerMessages';
import { guestPickerMessages } from '../../search/GuestPicker/guestPickerMessages';
import { destinationPickerMessages, SearchFormDestinationInput } from './SearchFormDestinationInput';
interface BaseProps {
  /** Check in date */
  checkIn: string;
  /** Check out date */
  checkOut: string;
  /** Rooms configuration */
  numberOfAdults: number;
  /** Rooms configuration */
  numberOfChildren: number;
  /** Rooms configuration */
  numberOfRooms: number;
  /** Callback for check-in date picker click */
  onCheckInDatePickerClick: () => void;
  /** Callback for check-out date picker click */
  onCheckOutDatePickerClick: () => void;
  /** Callback for Guest picker click */
  onGuestPickerClick: () => void;
  /** Callback for Submit click */
  onSubmit: () => void;
  /** Whether the check in picker popover is currently open */
  isCheckInPickerOpen: boolean;
  /** Whether the check out picker popover is currently open */
  isCheckOutPickerOpen: boolean;
  /** Whether the guest picker popover is currently open */
  isGuestPickerOpen: boolean;
  /** A ref to the guest picker container */
  guestPickerRef: React.RefObject<HTMLDivElement>;
  /** Whether the rooms count and label is shown */
  isRoomsLabelShown?: boolean;
  /** Whether the input icon label should be replaced with a text label */
  isTextLabel?: boolean;
  /** Whether the guest picker is hidden */
  hideGuestPicker?: boolean;
}
interface PropsWithDestination extends BaseProps {
  /** Destination name displayed in the destination button */
  destinationButtonDisplayValue: string;
  /** Destination name displayed in the destination input */
  destinationInputDisplayValue: string;
  /** Callback for Destination picker click */
  onDestinationPickerClick: (event: React.SyntheticEvent<Element, Event>) => void;
  /** A ref to the destination input container */
  destinationInputRef: React.RefObject<HTMLDivElement>;
  /** Whether the destination picker popover is currently open */
  isDestinationPickerOpen: boolean;
  /** Callback for suggestions requested */
  onSuggestionsRequested: (value: string, shouldLoadUserSearchHistory: boolean) => void;
  /** A flag to see if the destination input has error */
  destinationHasError: boolean;
}
interface PropsWithoutDestination extends BaseProps {
  destinationButtonDisplayValue: never;
  destinationInputDisplayValue: never;
  onDestinationPickerClick: never;
  destinationInputRef: never;
  isDestinationPickerOpen: never;
  onSuggestionsRequested: never;
  destinationHasError: never;
}
export type Props = PropsWithDestination | PropsWithoutDestination;
export const FormContainer = /*#__PURE__*/styled('div')({
  name: "FormContainer",
  class: "f1efzcun",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => Grid;
const SearchFormGrid = /*#__PURE__*/styled(_exp3())({
  name: "SearchFormGrid",
  class: "s1llasv",
  propsAsIs: true
});
const _exp5 = /*#__PURE__*/() => Grid;
const DestinationWrapper = /*#__PURE__*/styled(_exp5())({
  name: "DestinationWrapper",
  class: "d1wxv07i",
  propsAsIs: true
});
const _exp7 = /*#__PURE__*/() => Grid;
const DateWrapper = /*#__PURE__*/styled(_exp7())({
  name: "DateWrapper",
  class: "dtc2v11",
  propsAsIs: true
});
const _exp9 = /*#__PURE__*/() => Grid;
const GuestWrapper = /*#__PURE__*/styled(_exp9())({
  name: "GuestWrapper",
  class: "g1von4lg",
  propsAsIs: true
});
const _exp11 = /*#__PURE__*/() => Grid;
const SearchButtonWrapper = /*#__PURE__*/styled(_exp11())({
  name: "SearchButtonWrapper",
  class: "sr2n7b7",
  propsAsIs: true
});
const IconWrapper = /*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "i17c19d8",
  propsAsIs: false
});
const _exp12 = /*#__PURE__*/() => InputButton;
const FormButton = /*#__PURE__*/styled(_exp12())({
  name: "FormButton",
  class: "f8h5pd4",
  propsAsIs: true
});
const ButtonContent = /*#__PURE__*/styled('div')({
  name: "ButtonContent",
  class: "b1tpa1yl",
  propsAsIs: false
});
const _exp38 = /*#__PURE__*/() => Text;
export const LabelWrapper = /*#__PURE__*/styled(_exp38())({
  name: "LabelWrapper",
  class: "lbjw6k0",
  propsAsIs: true
});
export const SearchFormHorizontalFullWidth = React.memo(function SearchFormHorizontalFullWidth({
  checkIn,
  checkOut,
  destinationButtonDisplayValue,
  destinationInputDisplayValue,
  numberOfAdults,
  numberOfChildren,
  numberOfRooms,
  onDestinationPickerClick,
  onCheckInDatePickerClick,
  onCheckOutDatePickerClick,
  onGuestPickerClick,
  onSubmit,
  isDestinationPickerOpen,
  isCheckInPickerOpen,
  isCheckOutPickerOpen,
  isGuestPickerOpen,
  destinationInputRef,
  guestPickerRef,
  onSuggestionsRequested,
  destinationHasError = false,
  isRoomsLabelShown = true,
  isTextLabel = false
}: Props) {
  const {
    formatDate,
    formatMessage
  } = useIntl();
  const showDestination = Boolean(onDestinationPickerClick);
  const numberOfGuests = numberOfAdults + numberOfChildren;
  const guestMessage = formatMessage(guestPickerMessages.guest, {
    count: numberOfGuests
  });
  const roomsMessage = formatMessage(guestPickerMessages.room, {
    count: numberOfRooms
  });
  const checkInDate = checkIn && formatDate(dateStringToMiddayDate(checkIn), {
    day: '2-digit',
    month: 'short'
  });
  const checkOutDate = checkOut && formatDate(dateStringToMiddayDate(checkOut), {
    day: '2-digit',
    month: 'short'
  });
  return <FormContainer>
        <SearchFormGrid container spacing="s500" wrap="nowrap">
          {showDestination && <>
              <DestinationWrapper>
                <FormButton size="md" id="DestinationButton" dataId="DestinationButton" onClick={onDestinationPickerClick} icon={!isTextLabel ? <IconWrapper>
                        <Icon name="Pin" />
                      </IconWrapper> : undefined} placeholder={formatMessage(destinationPickerMessages.destinationPlaceholder)} className={cx(isDestinationPickerOpen && 'isActive', destinationHasError && 'hasError')}>
                  {(isTextLabel || destinationButtonDisplayValue) && <>
                      {isTextLabel && <LabelWrapper variant="labelS">
                          <FormattedMessageWrapper id="destination" defaultMessage="Destination" />
                        </LabelWrapper>}

                      {destinationButtonDisplayValue && <ButtonContent data-id="DestinationValue">
                          {destinationButtonDisplayValue}
                        </ButtonContent>}
                    </>}
                </FormButton>
                {isDestinationPickerOpen && <div ref={destinationInputRef}>
                    <SearchFormDestinationInput focusOnMount shouldLoadUserSearchHistory destination={destinationInputDisplayValue} onSuggestionsRequested={onSuggestionsRequested} isDestinationPickerOpen={isDestinationPickerOpen} isTextLabel={isTextLabel} />
                  </div>}
              </DestinationWrapper>
            </>}
          <DateWrapper>
            <FormButton size="md" id="CheckInInput" dataId="CheckInInput" onClick={onCheckInDatePickerClick} icon={!isTextLabel ? <IconWrapper>
                    <Icon name="CalendarStart" />
                  </IconWrapper> : undefined} placeholder={formatMessage(datePickerMessages.checkInDate)} className={cx(isCheckInPickerOpen && 'isActive')}>
              {isTextLabel && <LabelWrapper variant="labelS">
                  <FormattedMessageWrapper id="checkIn" defaultMessage="Check-in" />
                </LabelWrapper>}
              {checkInDate && <ButtonContent>{checkInDate}</ButtonContent>}
            </FormButton>
          </DateWrapper>
          <DateWrapper>
            <FormButton size="md" id="CheckOutInput" dataId="CheckOutInput" onClick={onCheckOutDatePickerClick} icon={!isTextLabel ? <IconWrapper>
                    <Icon name="CalendarEnd" />
                  </IconWrapper> : undefined} placeholder={formatMessage(datePickerMessages.checkOutDate)} className={cx(isCheckOutPickerOpen && 'isActive')}>
              {isTextLabel && <LabelWrapper variant="labelS">
                  <FormattedMessageWrapper id="checkOut" defaultMessage="Check-out" />
                </LabelWrapper>}
              <ButtonContent>{checkOutDate && checkOutDate}</ButtonContent>
            </FormButton>
          </DateWrapper>

          <GuestWrapper>
            <div ref={guestPickerRef}>
              <FormButton size="md" id="GuestInput" dataId="GuestInput" onClick={onGuestPickerClick} icon={!isTextLabel ? <IconWrapper>
                      <Icon name="People" />
                    </IconWrapper> : undefined} className={cx(isGuestPickerOpen && 'isActive')}>
                {isTextLabel && <LabelWrapper variant="labelS">
                    <FormattedMessageWrapper id="guests" defaultMessage="Guests" />
                  </LabelWrapper>}
                <ButtonContent>
                  {guestMessage}
                  {isRoomsLabelShown && <>, {roomsMessage}</>}
                </ButtonContent>
              </FormButton>
            </div>
          </GuestWrapper>
          <SearchButtonWrapper>
            <Button size="lg" fullWidth dataId="SearchButton" onClick={onSubmit} iconStart={<Icon name="Search" />}>
              <FormattedMessageWrapper id="search" defaultMessage="Search" />
            </Button>
          </SearchButtonWrapper>
        </SearchFormGrid>
      </FormContainer>;
});

require("./SearchFormHorizontalFullWidth.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchFormHorizontalFullWidth.tsx");